/**
 * SourceChangeEvent — a detected change in an already-archived official source.
 *
 * RFC-0004 Phase 1a (SENSE). The scheduled ingest re-fetches an archived CELLAR
 * source, recomputes its sha256, and compares it against the stored archival
 * hash. A mismatch is a SourceChangeEvent — NOT a verdict (ADR-0002): it is an
 * advisory candidate that must pass through the reversible review queue
 * (ADR-0004) before any canonical effect.
 *
 * Provenance discipline (ADR-0002 red line #4 / KAIYANG §11): the event carries
 * the official `sourceUrl` plus BOTH hashes (`prevSha256` → `newSha256`) so the
 * change is reproducible from the content-addressed R2 archive.
 */

import { z } from "zod";

const Sha256 = z.string().regex(/^[a-f0-9]{64}$/);

export const SourceChangeEventSchema = z
  .object({
    // Identity of the archived source that changed (source-registry.v0.1.json id).
    sourceRegistryId: z.string().min(1),
    // Provenance (CI red line #4): official URL + content hashes, both required.
    sourceUrl: z.string().url(),
    prevSha256: Sha256,
    newSha256: Sha256,
    // Always "source_amendment" in Phase 1a; widened in Phase 2 (draft sources).
    changeKind: z.literal("source_amendment"),
    // Injected by the caller (no Date.now() in pure detection) — ISO 8601.
    detectedAt: z.string().min(1),
  })
  .refine((e) => e.prevSha256 !== e.newSha256, {
    message: "SourceChangeEvent requires prevSha256 !== newSha256 (a real change)",
    path: ["newSha256"],
  });
export type SourceChangeEvent = z.infer<typeof SourceChangeEventSchema>;
